// ==============================================
// Admin Layout
// ==============================================

import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  ArrowLeftIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../providers/AuthProvider';
import { AdminMenuNav } from '../components/admin/AdminMenuNav';

export function AdminLayout() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'w-64 bg-slate-800/50 border-r border-slate-700 flex flex-col',
        'fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-in-out md:relative md:translate-x-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        {/* Logo */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div>
              <h1 className="font-bold text-white">Admin Panel</h1>
              <p className="text-xs text-slate-400">Vortex Marine</p>
            </div>
          </div>
          <button
            type="button"
            className="md:hidden p-1 text-slate-400 hover:text-white"
            onClick={closeSidebar}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Back to App — pinned at the top */}
        <div className="px-4 pt-4 pb-2 border-b border-slate-700">
          <NavLink
            to="/dashboard"
            onClick={closeSidebar}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-700/50 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back to App</span>
          </NavLink>
        </div>

        {/* Navigation — the shared admin menu (saved Menu Builder config) */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <AdminMenuNav accent="red" onNavigate={closeSidebar} />
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-x-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-slate-800/30 border-b border-slate-700 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="md:hidden p-2 text-slate-400 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            <h2 className="text-lg font-semibold text-white">Administration</h2>
          </div>

          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-block px-2 py-1 text-xs font-medium bg-red-500/20 text-red-400 rounded">
              {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </span>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full rounded-full" />
                ) : (
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || 'A'}
                  </span>
                )}
              </div>
              <span className="hidden sm:inline text-sm text-slate-300">{user?.name || user?.email}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default AdminLayout;
